// Пропорциональное сжатие колонок оболочки — та же механика, что у flex-shrink,
// но посчитанная в JS: колонки живут в разных поддеревьях (левая панель курса —
// внутри `WorkspaceShell`, панель репетитора — `position: fixed` у правого края,
// центр отжимается `margin-right` у `<main>`), поэтому браузер их вместе не
// раскладывает и договориться о ширинах некому.
//
// Зачем вообще: раньше узкое окно РЕЗАЛО панель — центр переставал помещаться, и
// левая колонка схлопывалась целиком (на дефолтах это происходило уже на 1351px,
// то есть у части ноутбуков курс открывался без панели плана). Сжимать колонки
// до их минимумов — мягче и обратимо, а сворачивание остаётся ручным действием
// пользователя.
//
// Правило одно: у каждой колонки есть желаемая ширина (`pref` — предпочтение
// пользователя или дефолт) и минимум (`min`). Если сумма желаемых не влезает,
// дефицит раскладывается ПРОПОРЦИОНАЛЬНО запасу сжатия (`pref − min`) — колонка
// с большим запасом отдаёт больше. За один проход, без итераций: доля колонки
// никогда не превышает её собственного запаса, потому что
// `deficit * cap_i / Σcap ≤ cap_i` при `deficit ≤ Σcap`.

export interface ShrinkTrack {
	/** Желаемая ширина, px: настройка пользователя либо дефолт. */
	pref: number;
	/** Ниже этой ширины колонку не сжимать, px. */
	min: number;
}

/**
 * Разложить `available` по колонкам.
 *
 * Возвращает ширины в порядке входного массива. Если желаемое помещается —
 * отдаёт `pref` как есть (излишек забирает центр, он `flex-1`). Если дефицит
 * больше суммарного запаса — все колонки уходят в `min`, и переполнение остаётся
 * на совести вызывающего: ниже `lg` боковые колонки не рендерятся вовсе, а на
 * desktop минимумы подобраны так, чтобы сумма влезала ровно в 1024px.
 */
export function shrinkToFit(tracks: readonly ShrinkTrack[], available: number): number[] {
	const deficit = tracks.reduce((sum, track) => sum + track.pref, 0) - available;
	if (deficit <= 0) return tracks.map((track) => track.pref);

	const capacity = tracks.map((track) => Math.max(0, track.pref - track.min));
	const capacitySum = capacity.reduce((sum, value) => sum + value, 0);
	// Сжимать нечего — колонки уже на минимуме. Возвращаем как есть, а не 0:
	// «не влезает» не повод схлопнуть панель в ничто.
	if (capacitySum <= 0) return tracks.map((track) => track.pref);

	// Дробная доля дефицита. Больше единицы не берём — иначе колонка ушла бы ниже
	// собственного минимума.
	const ratio = Math.min(1, deficit / capacitySum);
	return tracks.map((track, index) => Math.round(track.pref - (capacity[index] ?? 0) * ratio));
}
